import React, { useEffect } from "react"

import { Form, Input, Label, Row, Col, FormFeedback } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import Spinners from "components/Common/Spinner"
import { useStoreAdditionalDetailsProfileMutation } from "store/api/profile/profile.api"
import { useSelector } from "react-redux"
import AdditionalDetailsGstinUpload from "./additionalDetailsGstinUpload"

const AdditionalDetails = ({ kycData, profileData }) => {
  const [mutate, { isLoading }] = useStoreAdditionalDetailsProfileMutation()
  const { userType } = useSelector((state) => state.user)

  const storeDetails = async (body) => {
    try {
      const { success, message } = await mutate({
        ...body,
        kycStep: kycData.status,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "toastMessage" })
        window.location.reload()
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      primary_address: profileData?.additionalDetails?.primary_address,
      secondary_address: profileData?.additionalDetails?.secondary_address,
      is_secondary_address_same:
        profileData?.additionalDetails?.is_secondary_address_same || "SAME",
      business_trade_name: profileData?.additionalDetails?.business_trade_name,
      gstin: profileData?.additionalDetails?.gstin,
      gst_certificate: profileData?.additionalDetails?.gst_certificate,
    },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      storeDetails(values)
    },
  })

  useEffect(() => {
    const { is_secondary_address_same, primary_address, secondary_address } =
      validation.values

    if (is_secondary_address_same === "SAME") {
      if (secondary_address !== primary_address) {
        validation.setFieldValue(
          "secondary_address",
          primary_address || "",
          false
        )
      }
    }
  }, [
    validation.values.is_secondary_address_same,
    validation.values.primary_address,
  ])

  return (
    <>
      <div className="bank-details-wrapper">
        <h5 className="title mb-3">Additional Details</h5>
        <p>
          These details are required for registration of{" "}
          {userType === "INVESTOR" ? "an investor" : "a Wealth Partner"}
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Row className="mb-2">
            <Col xl={12}>
              <Label className="col-form-label">
                Name <span className="error">*</span>
              </Label>
              <Input
                name="name"
                disabled={profileData?.investorsAadhaar?.name}
                type="text"
                className="form-control"
                id="horizontal-firstname-Input"
                placeholder={`Enter Name`}
                value={profileData?.investorsAadhaar?.name}
              />
            </Col>
            <Col xl={12}>
              <Label className="col-form-label">
                Primary Address <span className="error">*</span>
              </Label>

              <Input
                name="primary_address"
                type="textarea"
                className="form-control"
                disabled={profileData?.investorsAadhaar?.primary_address}
                id="horizontal-firstname-Input"
                placeholder={`Enter Primary Address`}
                value={validation.values.primary_address}
                onChange={validation.handleChange}
                invalid={
                  validation.touched.primary_address &&
                  validation.errors.primary_address
                }
              />

              {validation.touched.primary_address &&
              validation.errors.primary_address ? (
                <FormFeedback type="invalid">
                  {validation.errors.primary_address}
                </FormFeedback>
              ) : null}
            </Col>

            <Col xl={12}>
              <Label className="col-form-label">
                Is Secondary Address same as Primary Address?
                <span className="error">*</span>
              </Label>
              <div className="d-flex flex-row gap-2 cp">
                <Input
                  className="cp"
                  name="is_secondary_address_same"
                  type="radio"
                  value={"SAME"}
                  checked={
                    validation.values.is_secondary_address_same === "SAME"
                  }
                  onChange={validation.handleChange}
                  id="comm_address_same"
                />
                <Label for="comm_address_same">Yes</Label>
              </div>
              <div className="d-flex flex-row gap-2 cp">
                <Input
                  className="cp"
                  name="is_secondary_address_same"
                  type="radio"
                  value={"DIFFERENT"}
                  checked={
                    validation.values.is_secondary_address_same === "DIFFERENT"
                  }
                  onChange={validation.handleChange}
                  id="secondary_address_different"
                />
                <Label for="secondary_address_different">No</Label>
              </div>
            </Col>
            <Col xl={12}>
              <Label className="col-form-label">
                Secondary Address <span className="error">*</span>
              </Label>

              <Input
                name="secondary_address"
                type="textarea"
                className="form-control"
                disabled={
                  validation.values.is_secondary_address_same === "SAME"
                }
                id="horizontal-firstname-Input"
                placeholder={`Enter Secondary Address`}
                value={validation.values.secondary_address}
                onChange={validation.handleChange}
                invalid={
                  validation.touched.secondary_address &&
                  validation.errors.secondary_address
                }
              />

              {validation.touched.secondary_address &&
              validation.errors.secondary_address ? (
                <FormFeedback type="invalid">
                  {validation.errors.secondary_address}
                </FormFeedback>
              ) : null}
            </Col>
            <Col xl={12}>
              <Label className="col-form-label">
                Trade Name of the Business, if any
              </Label>

              <Input
                name="business_trade_name"
                type="text"
                className="form-control"
                id="horizontal-firstname-Input"
                placeholder={`Enter Trade Name of the Business`}
                value={validation.values.business_trade_name}
                onChange={validation.handleChange}
                invalid={
                  validation.touched.business_trade_name &&
                  validation.errors.business_trade_name
                }
              />

              {validation.touched.business_trade_name &&
              validation.errors.business_trade_name ? (
                <FormFeedback type="invalid">
                  {validation.errors.business_trade_name}
                </FormFeedback>
              ) : null}
            </Col>

            <Col xl={12}>
              <Label className="col-form-label">GSTIN, if applicable</Label>

              <Input
                name="gstin"
                type="text"
                className="form-control"
                id="horizontal-firstname-Input"
                placeholder={`Enter GSTIN`}
                value={validation.values.gstin}
                onChange={validation.handleChange}
                invalid={validation.touched.gstin && validation.errors.gstin}
              />

              {validation.touched.gstin && validation.errors.gstin ? (
                <FormFeedback type="invalid">
                  {validation.errors.gstin}
                </FormFeedback>
              ) : null}
            </Col>
            <Col xl={12}>
              <Label className="col-form-label">
                GST Certificate, if applicable
              </Label>
              <AdditionalDetailsGstinUpload
                fieldKey={"gst_certificate"}
                validation={validation}
                extensionAllowedInput={".pdf"}
                extensionAllowed={["application/pdf"]}
                preview={true}
                kycData={profileData}
              />
            </Col>
            <Col sm={12}>
              <div className="d-flex align-items-center mt-4">
                <button type="submit" className="btn btn-primary w-100">
                  {isLoading ? <Spinners /> : "Proceed"}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  )
}

export default AdditionalDetails
